import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../core/client';
import type { Contract, ContractBody, ContractFilters } from './contracts.types';

const BASE = '/v1/contracts';
const KEY = ['contracts'] as const;

export function useContracts(filters: ContractFilters = {}) {
  const qs = new URLSearchParams();
  if (filters.asset_id) qs.set('asset_id', filters.asset_id);
  if (filters.status) qs.set('status', filters.status);
  if (filters.contract_type) qs.set('contract_type', filters.contract_type);
  const s = qs.toString();
  return useQuery({
    queryKey: [...KEY, filters],
    queryFn: () => api.get<Contract[]>(s ? `${BASE}?${s}` : BASE),
  });
}

/** Contracts covering one CI — the asset record's Contracts section. */
export function useAssetContracts(assetId: string | null | undefined) {
  return useQuery({
    queryKey: [...KEY, 'asset', assetId],
    queryFn: () => api.get<Contract[]>(`${BASE}?asset_id=${assetId}`),
    enabled: !!assetId,
  });
}

export function useCreateContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ContractBody) => api.post<Contract>(BASE, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ContractBody }) =>
      api.post<Contract>(`${BASE}/${id}/update`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** End a contract early — never a delete; the record stays for audit. */
export function useTerminateContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      api.post<Contract>(`${BASE}/${id}/terminate`, { note: note ?? null }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useAddContractCoverage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, assetId }: { id: string; assetId: string }) =>
      api.post<Contract>(`${BASE}/${id}/coverage/${assetId}`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useRemoveContractCoverage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, assetId }: { id: string; assetId: string }) =>
      api.delete<Contract>(`${BASE}/${id}/coverage/${assetId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
